import moment from "moment/moment";
import logger from "./logger";

export const changeDateFormatForServer = (date) => {
  return date ? moment(date).format("YYYY-MM-DD") : "";
};
export const changeDateTimeFormatForServer = (date) => {
  return date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "";
};

/**
 * Deep-walk a payload and replace every Date / moment instance with a
 * naive IST wall-clock string ("YYYY-MM-DD HH:mm:ss"), matching what the
 * backend expects (DB stores datetimes in IST, Carbon parses with default
 * tz Asia/Kolkata).
 *
 * Without this, JSON.stringify converts Dates to ISO-8601 with a `Z`
 * suffix (UTC), which Carbon then re-interprets and shifts by +05:30 on
 * write — corrupting clinical timestamps like patient_ipd_details.admit_at.
 *
 * Plain strings are passed through unchanged, so calendar fields already
 * pre-formatted with `changeDateFormatForServer` are safe.
 */
export const formatPayloadDatesForServer = (value) => {
  if (value === null || value === undefined) return value;
  if (value instanceof Date || moment.isMoment(value)) {
    return changeDateTimeFormatForServer(value);
  }
  if (Array.isArray(value)) {
    return value.map(formatPayloadDatesForServer);
  }
  if (typeof value === "object") {
    if (value instanceof FormData || value instanceof Blob || value instanceof File) {
      return value;
    }
    const out = {};
    for (const k of Object.keys(value)) {
      out[k] = formatPayloadDatesForServer(value[k]);
    }
    return out;
  }
  return value;
};
export const getTimeFormat = (date) => {
  return date ? moment(date).format("hh:mm A") : "";
};
export const changeDateFormatForShow = (date) => {
  return date ? moment(date).format("DD/MM/YYYY") : "";
};
export const changeDateFormatForDisplay = (date) => {
  return date ? moment(date).format("DD MMM YYYY") : "";
};
export const changeDateTimeFormatForDisplay = (date) => {
  return date ? moment(date).format("DD MMM YYYY, hh:mm A") : "";
};
// export const changeDateTimeFormatForDisplay = (date) => {
//     return date ? moment(date).format("DD MMM YYYY HH:mm A") : '';
// }
export function tomorrowDate() {
  return moment().add(1, "days").format("DD/MM/YYYY");
}
export const convertToAmPm = (timeString) => {
  if (!timeString) return ""; // Handle empty or null values

  if (timeString.includes("AM") || timeString.includes("PM")) {
    return timeString;
  }

  const parts = timeString.split(":");
  if (parts.length < 2) return timeString;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) return timeString;

  const amPm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12; // Convert 0 to 12 for midnight

  return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${amPm}`;
};

export function getTimeFromTimestamp(timestamp) {
  // Check if timestamp is a Moment.js object and convert it to a string
  if (timestamp?._isAMomentObject) {
    timestamp = timestamp.format("YYYY-MM-DDTHH:mm:ss");
  }

  if (typeof timestamp !== "string") {
    console.error("Invalid timestamp format:", timestamp);
    return ""; // Return empty string to prevent rendering issues
  }

  const [, timePart] = timestamp.split("T") || [];

  if (!timePart) return ""; // Handle invalid format

  const [hours, minutes] = timePart.split(":");

  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12 || 12; // Convert 0 to 12-hour format

  return `${hour}:${minutes} ${ampm}`;
}

export function nowDateTime() {
  return moment().format("YYYY-MM-DD HH:mm:ss");
}

export function dateTime(hours = 0, date = moment()) {
  return moment(date).add(hours, "hours").format("YYYY-MM-DD HH:mm:ss");
}

export function formatAndAddDate(days, format = "DD-MM-YYYY", date = new Date()) {
  return moment(date, format).add(days, "days");
}

export function momentToDate(date) {
  return moment(date)?.toDate();
}

export function momentYear() {
  return moment().year();
}

export function isBetween(beginTime, startTime, endTime) {
  return (
    moment(beginTime).isBetween(startTime, endTime) || moment(beginTime).isSame(moment(startTime))
  );
}

export function momentResult(input) {
  return moment(input);
}

export function timeFormat(time, format = "h:mm") {
  return moment(time, format);
}

export function momentDuration(input) {
  return moment.duration(input);
}

export function isTodayDate(date) {
  return moment(date, "YYYY-MM-DD").isSame(moment(), "day");
}

export function isPastDate(date) {
  return moment(date, "YYYY-MM-DD").isBefore(moment(), "day");
}

export function isFutureDate(date) {
  return moment(date, "YYYY-MM-DD").isAfter(moment(), "day");
}

export const isSameDay = (date1, date2) =>
  date1?.getFullYear() === date2?.getFullYear() &&
  date1?.getMonth() === date2?.getMonth() &&
  date1?.getDate() === date2?.getDate();

export const formatTimeToHHMM = (dateTimeString) => {
  if (!dateTimeString) return "";

  // Extract time part from "YYYY-MM-DD HH:MM:SS"
  const timePart = dateTimeString.split(" ")[1]; // Get "HH:MM:SS"

  // Extract HH and MM only
  return timePart.substring(0, 5); // Get "HH:MM"
};

export function calculateAge(dob) {
  if (!dob || dob === "0000-00-00" || dob === "null" || dob === "undefined") return "";

  // 1. Try to parse with common formats (strict)
  let birthDate = moment(dob, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"], true);

  if (!birthDate.isValid()) {
    // 2. Try lax parsing (handles time parts "YYYY-MM-DD HH:mm:ss")
    birthDate = moment(dob);

    if (!birthDate.isValid()) {
      // 3. Last resort: native Date
      const nativeDate = new Date(dob);
      if (isNaN(nativeDate.getTime())) return "";
      birthDate = moment(nativeDate);
    }
  }

  const now = moment();

  // Handle future dates
  if (birthDate.isAfter(now)) return "";

  const yrs = now.diff(birthDate, "years");
  if (isFinite(yrs) && yrs > 0) return `${yrs}Y`;

  const months = now.diff(birthDate, "months");
  if (isFinite(months) && months > 0) return `${months}M`;

  const days = now.diff(birthDate, "days");
  if (isFinite(days) && days >= 0) return `${days}D`;

  return "";
}

export const getCurrentDate = () => {
  return moment().format("DD/MM/YYYY");
};

export const getCurrentTime = () => {
  return moment().format("HH:mm");
};

/**
 * Returns a formatted "Age/Gender" string for a patient data object.
 * Handles nested patient properties and sanitized age results.
 * @param {Object} data - The patient or visit record
 * @returns {string} e.g. "25Y/Female" or "-/-"
 */
export function getPatientAgeGender(data) {
  if (!data) return "-/-";

  // Check age/dob in multiple possible locations
  const dob =
    data.dob ||
    data.p_dob ||
    data.patient_dob ||
    data.pt_dob ||
    data.patient?.dob ||
    data.patient_detail?.dob ||
    data.patient?.patient_detail?.dob;

  let ageData =
    data.age && !data.age.toString().includes("NaN") ? data.age : calculateAge(dob) || "-";

  // Ensure age has a suffix if it's a raw number
  if (ageData !== "-" && !isNaN(ageData)) {
    ageData = `${ageData}Y`;
  }

  // Check gender in multiple possible locations
  const genderData =
    data.gender ||
    data.p_gender ||
    data.patient_gender ||
    data.pt_gender ||
    data.u_gender ||
    data.gender_type ||
    data.patient?.gender ||
    data.patient?.gender_type ||
    data.patient_detail?.gender ||
    data.patient?.patient_detail?.gender ||
    "-";

  logger.log(`${ageData}/${genderData}`);
  return `${ageData}/${genderData}`;
}

/**
 * Parse API dates for date pickers without UTC "off by one" for calendar days.
 * `new Date("2026-04-24")` is midnight UTC; in US timezones that is the previous local day.
 * Visit / next-visit fields are calendar dates — use the YYYY-MM-DD part as local date.
 *
 * @param {string | Date | null | undefined} dateStr
 * @param {any} [fallback=null]
 * @returns {Date | any}
 */
export const safeDate = (dateStr, fallback = null) => {
  if (
    dateStr === null ||
    dateStr === undefined ||
    dateStr.toString().trim().length < 1 ||
    dateStr === "0000-00-00"
  ) {
    return fallback;
  }
  if (dateStr instanceof Date) {
    return isNaN(dateStr.getTime()) ? fallback : dateStr;
  }
  const s = String(dateStr).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const y = parseInt(ymd[1], 10);
    const m = parseInt(ymd[2], 10) - 1;
    const d = parseInt(ymd[3], 10);
    const local = new Date(y, m, d);
    return isNaN(local.getTime()) ? fallback : local;
  }
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? fallback : date;
};

/**
 * Resolves the first valid date from the provided arguments and formats it as "YYYY-MM-DD".
 * Falls back to today's date if none are provided or valid.
 *
 * @param {...(string|Date|moment|null|undefined)} dates
 * @returns {string} Date formatted as "YYYY-MM-DD"
 */
export const resolveDate = (...dates) => {
  for (const d of dates) {
    if (d) {
      const formatted = changeDateFormatForServer(d);
      if (formatted && formatted !== "Invalid date") return formatted;
    }
  }
  return changeDateFormatForServer(new Date());
};
